import React from 'react';
import {
  IDate,
  IEkonomiskAnalysDates,
} from '../AnalysSettingsHandler/AnalysSettingsHandler';
import {
  isStandardMonth,
  datePartsToCompletedDateString,
  DateInputType,
} from '../../../../../../../Utils/dateUtils';
import moment from 'moment';
import { IDateError } from '../../../../../../../Components/DateTextEdit/DateTextEdit';

interface IProps {
  hasExtraPeriod?: (year: number) => boolean;
  children: (dateErrorContext: IDateErrorContext) => React.ReactNode;
}

export type DateElementType = 'from' | 'to';

interface IDateErrorContext {
  getDateError: (
    date: IDate,
    element: DateElementType,
    intervalTyp?: DateInputType,
    required?: boolean
  ) => IDateError | undefined;
  getDateCombinationError: (
    date: IDate,
    dateError: IDateError | undefined,
    element: DateElementType,
    dates: IEkonomiskAnalysDates,
    isInterval: boolean,
    dateInputType?: DateInputType
  ) => IDateError | undefined;
  getOtherDateElement: (element: DateElementType) => DateElementType;
}

class EkonomiskAnalysDateErrorHandler extends React.Component<IProps> {
  isValidDate = (
    date: IDate,
    intervalTyp: DateInputType
  ): { validYear: boolean; validMonth: boolean; validDate: boolean } => {
    let validYear =
      date.year !== undefined && date.year.toString().length === 4;
    let validMonth =
      intervalTyp !== DateInputType.year ? date.month !== undefined : true;
    let validDate =
      intervalTyp === DateInputType.yearMonthDay
        ? date.day !== undefined
        : true;

    const maxMonth = intervalTyp === DateInputType.yearMonthDay ? 12 : 13;
    if (date.year && date.month) {
      const month = Number(date.month);
      const hasExtraPeriod =
        this.props.hasExtraPeriod &&
        this.props.hasExtraPeriod(Number(date.year));
      if (month < 1 || month > maxMonth) {
        validMonth = false;
      } else if (month === 13 && !hasExtraPeriod) {
        validMonth = false;
      }
    }
    if (validMonth) {
      if (date.month && isStandardMonth(date.month)) {
        validDate = moment(datePartsToCompletedDateString(date)).isValid();
      }
    }

    return { validYear, validMonth, validDate };
  };

  getDateError = (
    date: IDate,
    element: DateElementType,
    intervalTyp?: DateInputType,
    required: boolean = false
  ): IDateError | undefined => {
    if (intervalTyp === undefined) {
      return undefined;
    }

    const { validYear, validMonth, validDate } = this.isValidDate(
      date,
      intervalTyp
    );

    return date.userString === '' && !required
      ? undefined
      : date.userString === '' && required
      ? { text: 'Ange period', type: DateInputType.year }
      : !validYear
      ? { text: 'Ogiltig period', type: DateInputType.year }
      : !validMonth
      ? { text: 'Ogiltig period', type: DateInputType.yearMonth }
      : !validDate
      ? { text: 'Ogiltig period', type: DateInputType.yearMonthDay }
      : undefined;
  };

  getDateCombinationError = (
    date: IDate,
    dateError: IDateError | undefined,
    element: DateElementType,
    dates: IEkonomiskAnalysDates,
    isInterval: boolean,
    intervalTyp?: DateInputType
  ): IDateError | undefined => {
    let combinationError: IDateError | undefined;
    const otherElement = this.otherElement(element);
    if (!dateError && isInterval) {
      if (element === 'from') {
        combinationError = this.compareFromToDates(
          date,
          dates[otherElement],
          this.getErrorElementText(element),
          dateError
        );
      } else {
        combinationError = this.compareFromToDates(
          dates[otherElement],
          date,
          this.getErrorElementText(element),
          dateError
        );
      }
    }

    const otherDates = dates[otherElement];
    if (
      !this.getDateError(
        otherDates,
        otherElement,
        intervalTyp,
        otherElement === 'from'
      )
    ) {
      otherDates.error = undefined;
    }

    return combinationError;
  };

  getError = (
    date: IDate,
    element: DateElementType,
    dates: IEkonomiskAnalysDates,
    isInterval: boolean,
    dateInputType?: DateInputType,
    required?: boolean
  ) => {
    const dateError = this.getDateError(date, element, dateInputType, required);
    const combinationError = this.getDateCombinationError(
      date,
      dateError,
      element,
      dates,
      isInterval,
      dateInputType
    );
    return dateError || combinationError;
  };

  getErrorElementText = (element: DateElementType) => {
    if (element === 'from') {
      return 'sent';
    } else {
      return 'tidigt';
    }
  };

  compareFromToDates = (
    from: IDate,
    to: IDate,
    elementText: string,
    error?: IDateError
  ): IDateError | undefined => {
    if (!from.year || !to.year || error) {
      return undefined;
    }

    if (from.year !== to.year) {
      return { text: 'Måste vara samma år', type: DateInputType.year };
    }

    if (from.year > to.year) {
      return {
        text: `För ${elementText} datum`,
        type: DateInputType.yearMonth,
      };
    } else if (from.month && to.month && from.month > to.month) {
      return {
        text: `För ${elementText} datum`,
        type: DateInputType.yearMonth,
      };
    } else if (
      from.day &&
      to.day &&
      from.month === to.month &&
      from.day > to.day
    ) {
      return {
        text: `För ${elementText} datum`,
        type: DateInputType.yearMonthDay,
      };
    }

    return undefined;
  };

  otherElement = (element: DateElementType): DateElementType => {
    return element === 'from' ? 'to' : 'from';
  };

  render() {
    return (
      <>
        {this.props.children({
          getDateError: this.getDateError,
          getDateCombinationError: this.getDateCombinationError,
          getOtherDateElement: this.otherElement,
        })}
      </>
    );
  }
}

export default EkonomiskAnalysDateErrorHandler;
